import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser, isReadOnlyGuest } from "@/lib/roles";
import { api, formatApiErrorDetail } from "@/lib/api";
import CartDrawer from "@/components/CartDrawer";
import Pagination from "@/components/Pagination";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus, Sparkles, LayoutDashboard, Phone, Mail, Clock, Navigation, Pencil, LogOut, Menu, Lock, Flame } from "lucide-react";
import OperatorLoginModal from "@/components/OperatorLoginModal";
import { BundleTicker, BundleOverlay, BundleUpsellOverlay } from "@/components/BundleShop";
import TrendingBanner from "@/components/TrendingBanner";
import FeaturedEventBanner from "@/components/FeaturedEventBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { useResponsivePageSize } from "../hooks/useResponsivePageSize";
import { fetchAvailability } from "@/lib/reservation";

function ShopBody() {
  const { add, addBundle, setContext } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);
  const readOnlyGuest = isReadOnlyGuest(user);
  const isAuthed = !!user;
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [operatorOpen, setOperatorOpen] = useState(false);

  // Dynamic Bundles state
  const [bundles, setBundles] = useState([]);
  const [openBundle, setOpenBundle] = useState(null);          // shown from ticker click
  const [upsell, setUpsell] = useState(null);                  // { product, bundle } — shown when a bundled product is clicked
  // Trending This Week — set of product ids for "Trending" badge
  const [trendingIds, setTrendingIds] = useState(new Set());
  const handleTrendingIds = React.useCallback((ids) => setTrendingIds(new Set(ids)), []);

  // Build quick lookup: product_id → first active bundle that includes it
  const bundleByProduct = useMemo(() => {
    const map = new Map();
    bundles.forEach((b) => (b.products || []).forEach((p) => {
      if (!map.has(p.id)) map.set(p.id, b);
    }));
    return map;
  }, [bundles]);

  // Admin quick-edit dialog
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const PAGE_SIZE = useResponsivePageSize(8, 30);

  const loadCatalog = () => axios.get(`${API}/shop/catalog`).then((r) => setProducts(r.data));
  const loadBundles = () => axios.get(`${API}/bundles/public`).then((r) => setBundles(r.data)).catch(() => setBundles([]));

  useEffect(() => {
    setContext("retail");
    Promise.all([loadCatalog(), loadBundles()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // A customer clicked a product card. If that product is part of an active
  // bundle, show the upsell overlay. Otherwise just drop it in the cart.
  const handleProductClick = (product) => {
    const b = bundleByProduct.get(product.id);
    if (b) {
      setUpsell({ product, bundle: b });
    } else {
      add(product, 1);
      toast.success(`Added ${product.name}`);
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      name: p.name || "",
      description: p.description || "",
      wholesale_price: p.wholesale_price ?? 0,
      retail_price: p.retail_price ?? 0,
      warehouse_stock: p.warehouse_stock ?? 0,
      image_url: p.image_url || "",
      categories: p.categories || "",
    });
  };
  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      await api.put(`/products/${editing.id}`, {
        ...editForm,
        wholesale_price: Number(editForm.wholesale_price) || 0,
        retail_price: Number(editForm.retail_price) || 0,
        warehouse_stock: Number(editForm.warehouse_stock) || 0,
      });
      toast.success("Product updated");
      setEditing(null); setEditForm(null);
      await loadCatalog();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setSavingEdit(false); }
  };

  const cats = useMemo(() => {
    const s = new Set();
    products.forEach((p) => (p.categories || "").split(",").map((c) => c.trim()).filter(Boolean).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (needle && !`${p.name} ${p.sku}`.toLowerCase().includes(needle)) return false;
      if (cat && !(p.categories || "").toLowerCase().includes(cat.toLowerCase())) return false;
      return true;
    });
  }, [products, q, cat]);

  // Reset to page 0 whenever the filter/search changes so the user always
  // sees results from the top — matches Inventory / Clients behaviour.
  useEffect(() => { setPage(0); }, [q, cat]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // Live reservation availability for the visible page (what other shoppers have locked).
  const [avail, setAvail] = useState({});
  const pagedIds = paged.map((p) => p.id).join(",");
  useEffect(() => {
    const ids = pagedIds ? pagedIds.split(",") : [];
    if (!ids.length) return;
    let active = true;
    const load = () => fetchAvailability(ids).then((d) => { if (active) setAvail(d); });
    load();
    window.addEventListener("cart-updated", load);
    const t = setInterval(load, 20000);
    return () => { active = false; window.removeEventListener("cart-updated", load); clearInterval(t); };
  }, [pagedIds]);

  const countFor = (c) =>
    products.filter((p) => (p.categories || "").toLowerCase().includes(c.toLowerCase())).length;

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-[var(--bg)]">
      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="shop-sidebar-backdrop"
        />
      )}

      {/* Dark sidebar — brands scope pane, mirrors DashboardLayout */}
      <aside
        className={`w-64 lg:w-60 sidebar-dark flex flex-col h-full shrink-0
                    fixed inset-y-0 left-0 z-40 transform transition-transform duration-200
                    lg:static lg:translate-x-0
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="shop-sidebar"
      >
        <div className="px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/login" className="shrink-0 hover:opacity-85 transition-opacity" data-testid="shop-logo-link">
              <img src="/brand/mpire-logo.png" alt="MPIRE Distribution" className="w-12 h-12 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]" />
            </Link>
            <div className="min-w-0 flex flex-col justify-center leading-none">
              <Link to="/login" className="font-bold tracking-tight text-white text-[15px] hover:text-white/85 transition-colors" data-testid="shop-app-name">MPower!</Link>
              <a href="https://2conmedia.com" target="_blank" rel="noopener noreferrer" className="mt-1.5 block whitespace-nowrap text-[8px] uppercase tracking-[0.16em] text-gray-400 hover:text-amber-400 hover:underline underline-offset-2" data-testid="shop-powered-by-link">BY 2CONMEDIA.COM</a>
            </div>
          </div>
        </div>

        <div className="px-3 pt-3">
          <Link to="/" className="sidebar-link" data-testid="shop-back-to-app">
            <LayoutDashboard size={14} /> Main App
          </Link>
        </div>

        <div className="px-4 pt-4 pb-2">
          <p className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold">Brands</p>
        </div>

        <nav className="flex-1 p-3 pt-1 space-y-1 overflow-y-auto" data-testid="shop-brand-nav">
          <button
            onClick={() => { setCat(""); setMobileOpen(false); }}
            className={`sidebar-link w-full text-left ${!cat ? "active" : ""}`}
            data-testid="brand-all"
          >
            <Sparkles size={14} />
            <span className="flex-1">All brands</span>
            <span className="text-[10px] opacity-60 tabular-nums">{products.length}</span>
          </button>
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => { setCat(cat === c ? "" : c); setMobileOpen(false); }}
              className={`sidebar-link w-full text-left capitalize ${cat === c ? "active" : ""}`}
              data-testid={`brand-${c.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="flex-1 truncate">{c}</span>
              <span className="text-[10px] opacity-60 tabular-nums shrink-0">{countFor(c)}</span>
            </button>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4 space-y-3 text-[13px] text-gray-300" data-testid="shop-store-info">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-semibold mb-1.5">Visit us</p>
            <p className="text-white font-semibold text-sm leading-tight">MPIRE Distribution</p>
            <p className="leading-snug mt-0.5">8905 Ballard Lane<br />Clinton, Maryland 20735</p>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=8905+Ballard+Lane+Clinton+MD+20735"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[var(--brand)] font-semibold mt-2 hover:underline"
              data-testid="get-directions-link"
            >
              <Navigation size={13} /> Get directions
            </a>
          </div>
          <div className="flex flex-col gap-1.5 pt-2 border-t border-white/5">
            <a href="tel:+12024271598" className="inline-flex items-center gap-1.5 hover:text-white" data-testid="store-phone">
              <Phone size={13} /> (202) 427-1598
            </a>
            <a href="mailto:contactus@mpiredistribution.biz" className="inline-flex items-center gap-1.5 hover:text-white truncate" data-testid="store-email">
              <Mail size={13} /> contactus@mpiredistribution.biz
            </a>
          </div>
          <div className="pt-2 border-t border-white/5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-semibold mb-1.5 flex items-center gap-1"><Clock size={12} /> Hours</p>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <span className="text-gray-400">Mon – Fri</span><span>11am – 6pm</span>
              <span className="text-gray-400">Saturday</span><span>8am – 3pm</span>
              <span className="text-gray-400">Sunday</span><span className="text-red-400">Closed</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 h-screen overflow-y-auto overflow-x-hidden flex flex-col">
        {/* Sticky region: search bar + bundle ticker travel together above the grid */}
        <div className="sticky top-0 z-40 bg-white">
          {/* Top bar: search + cart */}
          <header className="bg-white border-b border-[var(--border)]">
            <div className="px-3 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-md hover:bg-black/5 active:bg-black/10"
                aria-label="Open menu"
                data-testid="shop-mobile-menu-button"
              >
                <Menu size={22} />
              </button>
              <div className="flex-1" />
              <div className="relative flex-shrink-0">
                <Search size={14} className="absolute left-3 top-3 text-gray-400" />
                <Input
                  className="pl-8 w-40 sm:w-72"
                  placeholder="Search products…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="shop-search"
                />
              </div>
              <CartDrawer />
              {isAuthed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={async () => { await logout(); navigate("/login"); }}
                  data-testid="shop-logout-button"
                  title={`Sign out ${user.email}`}
                >
                  <LogOut size={14} className="mr-1.5" /> Logout
                </Button>
              )}
            </div>
          </header>

          {/* Dynamic Bundles ticker — bundles + Essence highlights.
              Sits INSIDE the sticky region so it stays pinned right below the
              search bar. Solid amber background + z-50 keeps it clear of the
              header's translucent layers. */}
          {bundles.length > 0 && (
            <BundleTicker bundles={bundles} onOpenBundle={(b) => setOpenBundle(b)} />
          )}
          <FeaturedEventBanner />
        </div>

        {/* Body — extra top spacing so "The Store." heading never crowds the ticker */}
        <section className="flex-1 px-3 sm:px-6 lg:px-8 pt-6 sm:pt-10 lg:pt-12 pb-4 sm:pb-8 lg:pb-10">
          <p className="label-eyebrow">Premium Hair Care Supply</p>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="shop-heading">The Store.</h1>
          <button
            type="button"
            onClick={() => setOperatorOpen(true)}
            className="mt-1.5 sm:mt-2 inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-[var(--brand)] transition-colors"
            data-testid="client-login-link"
          >
            <Lock size={11} strokeWidth={2.5} /> Client login
          </button>
          <a
            href="/essence"
            className="ml-3 mt-1.5 sm:mt-2 inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-[var(--brand)] transition-colors"
            data-testid="shop-essence-link"
          >
            <Sparkles size={11} strokeWidth={2.5} /> The Essence
          </a>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 max-w-2xl">
            Everything in stock, ready to ship. Prices in USD. Powered by MPIRE Distribution.
          </p>

          {cat && (
            <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 text-xs text-gray-600 bg-[var(--bg-soft)] px-3 py-1.5 rounded-full">
              <span className="capitalize">Filtered by <strong>{cat}</strong></span>
              <button onClick={() => setCat("")} className="text-[var(--brand)] font-semibold hover:underline">Clear</button>
            </div>
          )}

          {loading ? (
            <p className="text-gray-400 py-16 text-center">Loading catalog…</p>
          ) : (
            <>
              <div className="mt-4 sm:mt-6">
                <TrendingBanner priceKind="retail" onLoaded={handleTrendingIds} />
              </div>
              <div className="mt-4 sm:mt-6 lg:mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-2.5 gap-y-2 sm:gap-y-3" data-testid="shop-grid">
                {paged.map((p) => (
                  <ProductCard
                    key={p.id}
                    p={p}
                    reserved={(avail[p.id]?.available ?? p.warehouse_stock) <= 0}
                    inBundle={bundleByProduct.has(p.id)}
                    isTrending={trendingIds.has(p.id)}
                    onAdd={readOnlyGuest ? null : () => handleProductClick(p)}
                    onEdit={isAdmin ? () => openEdit(p) : null}
                  />
                ))}
                {paged.length === 0 && (
                  <p className="col-span-full text-center text-gray-400 py-16">No products match your search.</p>
                )}
              </div>
              <div className="mt-6 rounded-md border border-[var(--border)] overflow-hidden bg-white">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={filtered.length}
                  pageSize={PAGE_SIZE}
                  testid="shop-pagination"
                />
              </div>
            </>
          )}
        </section>

        <footer className="mt-auto border-t border-[var(--border)] py-4 text-center text-xs text-gray-500">
          Secure checkout by Stripe · © MPIRE Distribution
        </footer>
      </main>

      {/* Admin quick-edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="shop-edit-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">Edit · {editing?.name}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="shop-edit-name" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="shop-edit-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Wholesale</Label>
                  <Input type="number" step="0.01" value={editForm.wholesale_price} onChange={(e) => setEditForm({ ...editForm, wholesale_price: e.target.value })} data-testid="shop-edit-wholesale" />
                </div>
                <div>
                  <Label className="text-xs">Retail</Label>
                  <Input type="number" step="0.01" value={editForm.retail_price} onChange={(e) => setEditForm({ ...editForm, retail_price: e.target.value })} data-testid="shop-edit-retail" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Warehouse stock</Label>
                  <Input type="number" value={editForm.warehouse_stock} onChange={(e) => setEditForm({ ...editForm, warehouse_stock: e.target.value })} data-testid="shop-edit-stock" />
                </div>
                <div>
                  <Label className="text-xs">Categories</Label>
                  <Input value={editForm.categories} onChange={(e) => setEditForm({ ...editForm, categories: e.target.value })} data-testid="shop-edit-categories" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Image URL</Label>
                <Input value={editForm.image_url} onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })} placeholder="/api/uploads/products/…" data-testid="shop-edit-image-url" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setEditing(null); setEditForm(null); }} data-testid="shop-edit-cancel">Cancel</Button>
                <Button className="btn-brand" disabled={savingEdit} onClick={saveEdit} data-testid="shop-edit-save">
                  {savingEdit ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <OperatorLoginModal open={operatorOpen} onOpenChange={setOperatorOpen} />
    </div>
  );
}

function ProductCard({ p, onAdd, onEdit, inBundle, isTrending, reserved }) {
  return (
    <div className="group relative bg-white rounded-lg border border-[var(--border)] overflow-hidden hover:shadow-lg transition-all" data-testid={`product-card-${p.id}`}>
      {reserved && (
        <span
          className="absolute top-1.5 right-1.5 z-10 rounded-full bg-gray-800 text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest shadow-sm"
          data-testid={`reserved-badge-${p.id}`}
          title="All units are currently reserved in other shoppers' carts"
        >
          Reserved
        </span>
      )}
      {isTrending && (
        <span
          className={`absolute z-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm flex items-center gap-0.5 ${inBundle ? "top-8 left-1.5" : "top-1.5 left-1.5"}`}
          data-testid={`trending-badge-${p.id}`}
          title="One of this week's top sellers"
        >
          <Flame size={9} strokeWidth={2.75} /> Trending
        </span>
      )}
      {inBundle && (
        <span
          className="absolute top-1.5 left-1.5 z-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm"
          data-testid={`bundle-badge-${p.id}`}
          title="Part of a bundle deal"
        >
          Bundle
        </span>
      )}
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="absolute top-1.5 right-1.5 z-10 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm border border-[var(--border)] flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:bg-[var(--brand)] hover:text-white hover:border-[var(--brand)] transition-all shadow-sm"
          title="Edit product"
          data-testid={`edit-product-${p.id}`}
        >
          <Pencil size={12} />
        </button>
      )}
      <div className="aspect-square bg-[var(--bg-soft)] flex items-center justify-center overflow-hidden">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className="text-gray-300 text-4xl">·</div>
        )}
      </div>
      <div className="p-2 sm:p-3 md:p-2 lg:p-2.5">
        <div className="capitalize text-xs sm:text-sm md:text-[11px] lg:text-xs font-medium leading-tight line-clamp-2" data-testid={`product-name-${p.id}`}>{p.name}</div>
        <div className="text-[9px] sm:text-[10px] md:text-[8px] text-gray-500 font-mono mt-1 truncate">{p.sku}</div>
        <div className="mt-2 sm:mt-3 md:mt-1.5 flex items-center justify-between gap-1">
          <span className="text-base sm:text-lg lg:text-xl md:text-sm lg:text-base font-bold tabular-nums">${Number(p.retail_price).toFixed(2)}</span>
          <Button
            size="sm" onClick={onAdd} disabled={reserved || !onAdd} className="btn-brand h-7 sm:h-8 px-2 sm:px-3 text-xs disabled:opacity-40"
            data-testid={`add-to-cart-${p.id}`}
          ><Plus size={12} className="mr-0.5 sm:mr-1" /> {reserved ? "Reserved" : onAdd ? "Add" : "View only"}</Button>
        </div>
        <div className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5 sm:mt-1">{reserved ? "Reserved by another shopper" : `${p.warehouse_stock} in stock`}</div>
      </div>
    </div>
  );
}

export default function ShopPage() {
  return <CartProvider><ShopBody /></CartProvider>;
}
